/**
 * Shortcode processor for pricing page content
 *
 * Extends the String prototype to add shortcode processing capabilities
 * specifically for pricing page content. Allows for dynamic text replacement
 * of pricing information and employee counts.
 *
 * @example
 * ```ts
 * const text = "Base price: {{total}}";
 * const processed = text
 *   .shortcode
 *   .pricingPage('en')
 *   .applyTotal({ total: 100, totalDiscount: 80 })
 *   .toString();
 * // Returns: "Base price: $100"
 *
 * const employeeText = "For {{nbEmployee}} employees";
 * const processed = employeeText
 *   .shortcode
 *   .pricingPage('en')
 *   .applyNbEmployee({ nbEmployee: 5 })
 *   .toString();
 * // Returns: "For 5 employees"
 * ```
 *
 * Features:
 * - Chainable API for multiple transformations
 * - Locale-aware formatting
 * - Supports price and discount values
 * - Handles employee count replacements
 */
import { SiteLocale } from 'apps/website/types/dato';
import { applyTotal } from './transformers/applyTotal';
import { applyNbEmployee } from './transformers/applyNbEmployee';
import { applyOfferTitle } from './transformers/applyOfferTitle';
import { applySingularPlural } from './transformers/applySingularPlural';

export type ShortcodeContext = {
  text: string;
  locale: SiteLocale;
};

const createShortcodePricingPage = (text: string, locale: SiteLocale) => {
  const context: ShortcodeContext = { text, locale };

  return {
    applyTotal({
      total,
      totalDiscount,
    }: {
      total: number;
      totalDiscount?: number | null;
    }) {
      applyTotal({ context, total, totalDiscount });
      return this;
    },

    applyNbEmployee({ nbEmployee }: { nbEmployee: number }) {
      applyNbEmployee({ context, nbEmployee });
      return this;
    },

    applyOfferTitle({ offerTitle }: { offerTitle: string }) {
      applyOfferTitle({ context, offerTitle });
      return this;
    },

    applySingularPlural({ count }: { count: number }) {
      applySingularPlural({ context, count });
      return this;
    },

    toString() {
      return context.text;
    },
  };
};

declare global {
  interface String {
    shortcode: {
      pricingPage(
        locale: SiteLocale,
      ): ReturnType<typeof createShortcodePricingPage>;
    };
  }
}

// Initialiser l'objet shortcode
Object.defineProperty(String.prototype, 'shortcode', {
  get() {
    return {
      pricingPage: (locale: SiteLocale) =>
        createShortcodePricingPage(this.valueOf(), locale),
    };
  },
});
